<template>
  <div class="grade-progress">
    <!-- Header Filter Card -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters" class="filter-form">
        <el-form-item v-if="isCurrentUserAdmin && !props.adminMode" label="选择查看用户">
          <el-select v-model="filters.targetUserId" placeholder="选择用户/学校" @change="handleUserChange" filterable style="width: 280px">
            <el-option :key="currentUser?.id" :label="`${currentUser?.school || '本平台'} (当前用户)`" :value="currentUser?.id" />
            <el-option v-for="u in allUsers" :key="u.id" :label="`${u.school || '未填学校'} (${u.username})`" :value="u.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="当前学期">
          <el-select v-model="filters.semesterId" placeholder="选择学期" @change="fetchProgress" style="width: 220px">
            <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :icon="Refresh" @click="fetchProgress" :loading="loading">
            刷新进度
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Loading State -->
    <div v-loading="loading" v-if="loading && !progressData" class="loading-wrap"></div>

    <!-- Empty State -->
    <el-empty v-else-if="!progressData || progressData.students.total === 0" description="该用户本学期暂无数据。">
      <el-button v-if="!props.adminMode" type="primary" @click="$router.push('/grades')">前往成绩管理</el-button>
    </el-empty>

    <div v-else class="progress-dashboard">
      <!-- Top Overview Stats Cards -->
      <el-row :gutter="20" class="overview-cards">
        <el-col :span="8">
          <div class="kpi-card-premium students">
            <div class="card-icon">
              <el-icon><User /></el-icon>
            </div>
            <div class="card-body">
              <div class="card-title">已导入学生总数</div>
              <div class="card-value">{{ progressData.students.total }}<span class="unit">人</span></div>
              <div class="card-desc">分布在全校 {{ totalClassesCount }} 个班级中</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="kpi-card-premium teachers">
            <div class="card-icon">
              <el-icon><Reading /></el-icon>
            </div>
            <div class="card-body">
              <div class="card-title">已配置任课教师</div>
              <div class="card-value">{{ progressData.teachers.total_assignments }}<span class="unit">人次</span></div>
              <div class="card-desc">完成率：{{ teacherConfigProgressRate }}%</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="kpi-card-premium exams">
            <div class="card-icon">
              <el-icon><TrendCharts /></el-icon>
            </div>
            <div class="card-body">
              <div class="card-title">涉及考试类型</div>
              <div class="card-value">{{ Object.keys(progressData.grades.by_exam).length }}<span class="unit">场</span></div>
              <div class="card-desc">已启动成绩分析并导入了至少一科成绩</div>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- Main Layout Rows -->
      <el-row :gutter="20">
        <!-- Left: Student List Progress -->
        <el-col :span="12">
          <el-card class="progress-section-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span class="title-with-badge">
                  <el-icon class="header-icon"><User /></el-icon>
                  <strong>学生名单导入进度</strong>
                </span>
                <el-tag type="success" size="small" effect="plain">已就绪</el-tag>
              </div>
            </template>

            <div class="grade-collapse">
              <div v-for="(gradeInfo, gradeName) in progressData.students.by_grade" :key="gradeName" class="grade-item-box">
                <div class="grade-header-bar">
                  <span class="grade-title">{{ gradeName }}</span>
                  <div class="grade-meta">
                    <el-tag type="info" size="small">{{ gradeInfo.class_count }} 个班级</el-tag>
                    <el-tag type="primary" size="small">{{ gradeInfo.student_count }} 人已导入</el-tag>
                  </div>
                </div>

                <div class="class-grid">
                  <div v-for="(count, className) in gradeInfo.classes" :key="className" class="class-badge-item">
                    <span class="c-name">{{ className }}</span>
                    <span class="c-count">{{ count }} 人</span>
                  </div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- Right: Teacher Assignment Progress -->
        <el-col :span="12">
          <el-card class="progress-section-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span class="title-with-badge">
                  <el-icon class="header-icon"><Reading /></el-icon>
                  <strong>任课教师设置进度</strong>
                </span>
                <el-tag :type="teacherConfigProgressRate >= 100 ? 'success' : 'warning'" size="small">
                  配置进度: {{ teacherConfigProgressRate }}%
                </el-tag>
              </div>
            </template>

            <div class="teacher-list">
              <div v-for="(gradeInfo, gradeName) in progressData.students.by_grade" :key="gradeName" class="grade-teacher-box">
                <div class="grade-header-bar">
                  <span class="grade-title">{{ gradeName }} 任课列表</span>
                </div>

                <el-table :data="getTeacherTableData(gradeName, gradeInfo)" size="small" border stripe style="width: 100%">
                  <el-table-column prop="className" label="班级" width="100" align="center" />
                  <el-table-column label="配置状态" width="120" align="center">
                    <template #default="{ row }">
                      <el-tag :type="row.assignedCount > 0 ? (row.assignedCount >= 9 ? 'success' : 'warning') : 'danger'">
                        {{ row.assignedCount > 0 ? `已设 ${row.assignedCount} 科` : '未设置' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="教师映射详情">
                    <template #default="{ row }">
                      <div class="subject-teacher-tags" v-if="row.subjects.length > 0">
                        <el-tooltip v-for="item in row.subjects" :key="item.subject" :content="`${item.subject}: ${item.teacher}`" placement="top">
                          <span class="s-t-tag">
                            <strong>{{ item.subject }}</strong>: {{ item.teacher }}
                          </span>
                        </el-tooltip>
                      </div>
                      <span class="none-text" v-else>暂无任课教师分配记录</span>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Bottom Layout: Exam Grade Record Progress -->
      <el-row style="margin-top: 20px;">
        <el-col :span="24">
          <el-card class="progress-section-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span class="title-with-badge">
                  <el-icon class="header-icon"><TrendCharts /></el-icon>
                  <strong>考试成绩数据导入详情</strong>
                </span>
                <span class="header-desc">按考试类型与学科维度展现已录入的成绩条数和导入完整度</span>
              </div>
            </template>

            <div v-if="Object.keys(progressData.grades.by_exam).length === 0" class="no-exam-grades">
              <el-empty description="当前学期下暂无任何考试的成绩数据导入记录。请在上方或侧边栏切换至「成绩管理」进行成绩导入。">
                <el-button type="primary" @click="$router.push('/grades')">前往成绩管理</el-button>
              </el-empty>
            </div>

            <div v-else class="exam-grades-container">
              <div v-for="(examData, examType) in progressData.grades.by_exam" :key="examType" class="exam-block-premium">
                <div class="exam-header-bar">
                  <span class="exam-title-badge">
                    <el-icon><Calendar /></el-icon>
                    {{ getExamTypeLabel(examType) }}
                  </span>
                </div>

                <div v-for="(subjectsInfo, gradeName) in examData" :key="gradeName" class="grade-exam-detail-box">
                  <div class="grade-sub-header">
                    <span>{{ gradeName }}</span>
                    <el-tag size="small" type="info" effect="plain">
                      基准学生总数: {{ progressData.students.by_grade[gradeName]?.student_count || 0 }}人
                    </el-tag>
                  </div>

                  <el-row :gutter="16" class="subject-progress-grid">
                    <el-col v-for="(stats, subjectName) in subjectsInfo" :key="subjectName" :xs="24" :sm="12" :md="8" :lg="6">
                      <div class="subject-progress-card" :class="{ 'completed': stats.percentage >= 100 }">
                        <div class="subj-meta">
                          <span class="subj-name">{{ subjectName }}</span>
                          <span class="subj-fraction">{{ stats.imported_count }} / {{ stats.student_total }}人</span>
                        </div>
                        <el-progress :percentage="stats.percentage" :status="stats.percentage >= 100 ? 'success' : ''" :stroke-width="8" />
                        <div class="card-status-badge">
                          <el-icon v-if="stats.percentage >= 100" color="#67C23A"><CircleCheck /></el-icon>
                          <span v-else class="percent-label">{{ stats.percentage }}%</span>
                        </div>
                      </div>
                    </el-col>
                  </el-row>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, defineProps } from 'vue'
import { Refresh, User, Reading, TrendCharts, CircleCheck, Calendar } from '@element-plus/icons-vue'
import { gradeAnalysisApi, authApi } from '@/api'
import { ElMessage } from 'element-plus'
import { getUser, isAdmin } from '@/utils/auth'

const props = defineProps({
  adminMode: {
    type: Boolean,
    default: false
  },
  targetUserId: {
    type: Number,
    default: null
  }
})

const loading = ref(false)
const semesters = ref([])
const progressData = ref(null)
const currentUser = ref(getUser())
const isCurrentUserAdmin = computed(() => isAdmin())
const allUsers = ref([])

const filters = reactive({
  semesterId: null,
  targetUserId: null
})

const currentUserIdToQuery = computed(() => {
  return props.adminMode ? props.targetUserId : filters.targetUserId
})

// 计算班级总数
const totalClassesCount = computed(() => {
  if (!progressData.value) return 0
  let count = 0
  const grades = progressData.value.students.by_grade
  for (const key in grades) {
    count += grades[key].class_count
  }
  return count
})

// 计算教师设置进度百分比（假定每个班级应该有9门核心科目老师分配）
const teacherConfigProgressRate = computed(() => {
  if (!progressData.value || totalClassesCount.value === 0) return 0
  const totalExpected = totalClassesCount.value * 9
  const actual = progressData.value.teachers.total_assignments
  return Math.min(Math.round((actual / totalExpected) * 100), 100)
})

const getExamTypeLabel = (examType) => {
  const map = {
    monthly_1: '月考1',
    midterm: '期中考试',
    monthly_2: '月考2',
    final: '期末考试',
    mock_1: '一模',
    mock_2: '二模',
    mock_3: '三模',
    monthly_3: '月考3',
    diagnostic: '诊断性考试',
    joint_mock: '联合模拟'
  }
  return map[examType] || examType
}

// 格式化教师表格展示数据
const getTeacherTableData = (gradeName, gradeInfo) => {
  if (!progressData.value) return []
  const classList = Object.keys(gradeInfo.classes).sort()
  const gradeTeachers = progressData.value.teachers.by_grade[gradeName] || { classes: {} }
  
  return classList.map(cName => {
    const classAssigned = gradeTeachers.classes[cName] || { assigned_count: 0, subjects: [] }
    return {
      className: cName,
      assignedCount: classAssigned.assigned_count,
      subjects: classAssigned.subjects
    }
  })
}

const handleUserChange = async () => {
  filters.semesterId = null
  await fetchProgress()
}

const fetchProgress = async () => {
  loading.value = true
  try {
    const res = await gradeAnalysisApi.getProgress(filters.semesterId, currentUserIdToQuery.value)
    progressData.value = res
    semesters.value = res.semesters || []
    filters.semesterId = res.selected_semester_id
  } catch (e) {
    console.error(e)
    ElMessage.error('获取系统使用进度数据失败')
  } finally {
    loading.value = false
  }
}

watch(() => props.targetUserId, async (newVal) => {
  if (props.adminMode && newVal) {
    filters.semesterId = null
    await fetchProgress()
  }
})

onMounted(async () => {
  filters.targetUserId = currentUser.value?.id
  
  if (isCurrentUserAdmin.value && !props.adminMode) {
    try {
      const usersList = await authApi.getUsers()
      // 过滤掉当前管理员自己，防止重复
      allUsers.value = usersList.filter(u => u.id !== currentUser.value?.id)
    } catch (e) {
      console.error(e)
    }
  }
  
  await fetchProgress()
})
</script>

<style scoped>
.grade-progress {
  max-width: 1400px;
  margin: 0 auto;
  padding: 10px 0;
}

.filter-card {
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.loading-wrap {
  height: 300px;
}

/* KPI Cards Layout */
.overview-cards {
  margin-bottom: 20px;
}

.kpi-card-premium {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.025);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.kpi-card-premium:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.15);
}

.kpi-card-premium::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  transform: translate(30%, -30%);
  z-index: 0;
  opacity: 0.05;
}

.kpi-card-premium.students::after {
  background: #4f46e5;
}
.kpi-card-premium.teachers::after {
  background: #10b981;
}
.kpi-card-premium.exams::after {
  background: #f59e0b;
}

.kpi-card-premium .card-icon {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  z-index: 1;
}

.kpi-card-premium.students .card-icon {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-card-premium.teachers .card-icon {
  background: #ecfdf5;
  color: #059669;
}

.kpi-card-premium.exams .card-icon {
  background: #fffbeb;
  color: #d97706;
}

.kpi-card-premium .card-body {
  display: flex;
  flex-direction: column;
  z-index: 1;
}

.kpi-card-premium .card-title {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-card-premium .card-value {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.kpi-card-premium .card-value .unit {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin-left: 4px;
}

.kpi-card-premium .card-desc {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

/* Sections Layout */
.progress-section-card {
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-with-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #1e293b;
}

.header-icon {
  color: #4f46e5;
  font-size: 20px;
}

.header-desc {
  font-size: 13px;
  color: #64748b;
  font-weight: normal;
}

/* Left list: Student list boxes */
.grade-collapse {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.grade-item-box {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  padding: 16px;
}

.grade-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.grade-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  position: relative;
  padding-left: 8px;
}

.grade-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;
  width: 3px;
  background: #4f46e5;
  border-radius: 2px;
}

.grade-meta {
  display: flex;
  gap: 8px;
}

.class-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
}

.class-badge-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  transition: all 0.2s;
}

.class-badge-item:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}

.class-badge-item .c-name {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.class-badge-item .c-count {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

/* Right list: Teacher assignments */
.grade-teacher-box {
  margin-bottom: 20px;
}

.grade-teacher-box:last-child {
  margin-bottom: 0;
}

.subject-teacher-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.s-t-tag {
  font-size: 11px;
  padding: 2px 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  color: #475569;
}

.s-t-tag strong {
  color: #0f172a;
}

.none-text {
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
}

/* Exam Grade Records section */
.exam-block-premium {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
}

.exam-block-premium:last-child {
  margin-bottom: 0;
}

.exam-block-premium .exam-header-bar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.exam-title-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  background: #e0e7ff;
  color: #4f46e5;
  padding: 6px 14px;
  border-radius: 8px;
}

.grade-exam-detail-box {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.grade-exam-detail-box:last-child {
  margin-bottom: 0;
}

.grade-sub-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  color: #334155;
  font-size: 14px;
  border-bottom: 1px dashed #e2e8f0;
  padding-bottom: 8px;
  margin-bottom: 14px;
}

.subject-progress-grid {
  margin-top: 10px;
}

.subject-progress-card {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  padding: 12px 14px;
  position: relative;
  margin-bottom: 14px;
  transition: all 0.2s;
}

.subject-progress-card:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.subject-progress-card.completed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.subject-progress-card .subj-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.subject-progress-card .subj-name {
  font-weight: 700;
  font-size: 13px;
  color: #1e293b;
}

.subject-progress-card .subj-fraction {
  font-size: 11px;
  color: #64748b;
}

.subject-progress-card .card-status-badge {
  position: absolute;
  right: 12px;
  top: 12px;
  font-size: 16px;
}

.subject-progress-card .percent-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}

.no-exam-grades {
  padding: 40px 0;
}
</style>
